import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, X, Loader2, User } from "lucide-react";

const SuperadminQueue = () => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const getToken = () => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.token || "";
    } catch {
      return "";
    }
  };

  const fetchQueue = async () => {
    try {
      const token = getToken();
      if (!token) return;

      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/superadmin/pending-actions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setQueue(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Error fetching queue:", err.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, approve) => {
    try {
      const token = getToken();
      const decision = approve ? "approve" : "reject";
      setProcessingId(id);

      await axios.post(
        `${API_BASE}/api/superadmin/action/${id}`,
        { decision },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchQueue();
    } catch (err) {
      console.error("❌ Error processing action:", err.response?.data || err.message);
      alert("Failed to process action.");
    } finally {
      setProcessingId(null);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto text-gray-900 dark:text-gray-100">
      <h2 className="text-3xl font-bold mb-8 text-center text-gray-800 dark:text-gray-100">
        Superadmin Approval Panel
      </h2>

      {loading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse p-4 border rounded-lg bg-gray-100 dark:bg-gray-700 h-24"
            />
          ))}
        </div>
      )}

      {!loading && queue.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-center">🎉 No pending actions.</p>
      )}

      <div className="space-y-4">
        {queue.map((item) => {
          const isProcessing = processingId === item._id;

          return (
            <div
              key={item._id}
              className="p-5 border rounded-xl shadow-sm bg-white dark:bg-gray-800 flex flex-col md:flex-row justify-between items-start md:items-center hover:shadow-md transition"
            >
              {/* Left side details */}
              <div className="mb-3 md:mb-0 w-full">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-600 dark:text-white">
                    {item.action.toUpperCase()}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      item.status === "pending"
                        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-500 dark:text-gray-900"
                        : item.status === "approved"
                        ? "bg-green-100 text-green-700 dark:bg-green-600 dark:text-white"
                        : "bg-red-100 text-red-700 dark:bg-red-600 dark:text-white"
                    }`}
                  >
                    {item.status}
                  </span>
                </div>

                <p className="text-gray-800 dark:text-gray-200">
                  <span className="font-medium">Target:</span>{" "}
                  {item.targetUser?.email || "Unknown"}{" "}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({item.targetUser?.role || "N/A"})
                  </span>
                </p>
                <p className="text-gray-700 dark:text-gray-300 text-sm flex items-center gap-1 mt-1">
                  <User size={14} />
                  <span>
                    Requested by: {item.requestedBy?.username || "Unknown"} (
                    {item.requestedBy?.email || "N/A"})
                  </span>
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap mt-2 md:mt-0">
                <button
                  onClick={() => handleAction(item._id, true)}
                  disabled={isProcessing}
                  className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 transition disabled:opacity-50"
                >
                  {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Approve
                </button>
                <button
                  onClick={() => handleAction(item._id, false)}
                  disabled={isProcessing}
                  className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 transition disabled:opacity-50"
                >
                  {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                  Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SuperadminQueue;
